import type { WealthicaAccount, WealthicaConnection } from '../types';
// Using local enum to avoid import issues
const ConnectionProcessingState = {
  IDLE: 'idle' as const,
  SYNCING: 'syncing' as const,
  DISCONNECTING: 'disconnecting' as const
};

type ConnectionProcessingState = typeof ConnectionProcessingState[keyof typeof ConnectionProcessingState];
import { logger } from '@/services/LoggingService';
import { toast } from 'sonner';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import { supabase } from '@/integrations/supabase/client';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Helper function to handle save operations for Wealthica accounts
 * Extracted to reduce main function size and improve maintainability
 */
export async function executeSaveOperation(
  currentUserId: string,
  wealthicaConnectionData: WealthicaConnection,
  queryData: WealthicaAccount[],
  persistenceExecutionRef: React.MutableRefObject<Set<string>>,
  setProcessingState: (state: string) => void
): Promise<void> {
  if (!currentUserId || !wealthicaConnectionData || !queryData) {
    logger.error('executeSaveOperation', 'Cannot save - missing required data');
    return;
  }

  // Execution guard to prevent duplicate saves
  const executionKey = `save_${currentUserId}_${wealthicaConnectionData.institutionId}_${Date.now()}`;
  if (persistenceExecutionRef.current.has(executionKey)) {
    logger.warn('executeSaveOperation', 'Save already in progress, skipping duplicate');
    return;
  }
  
  persistenceExecutionRef.current.add(executionKey);
  
  try {
    setProcessingState(ConnectionProcessingState.SYNCING);
    
    logger.info('executeSaveOperation', 'Starting save operation', {
      accountsCount: queryData.length,
      userId: currentUserId,
      institutionId: wealthicaConnectionData.institutionId,
      executionKey
    });

    // Get bearer token from multi-store
    const bearerToken = useMultiWealthica2Store.getState().getConnection(wealthicaConnectionData.institutionId)?.bearerToken;
    
    if (!bearerToken) {
      logger.error('executeSaveOperation', 'No bearer token available', {
        storeMode: 'multi-store',
        institutionId: wealthicaConnectionData.institutionId
      });
      throw new Error('Bearer token not available for save operation');
    }
    
    const { error } = await supabase.functions.invoke('save-wealthica-data', {
      body: {
        userId: currentUserId,
        connectionData: wealthicaConnectionData,
        accountsData: queryData,
        bearerToken
      }
    });
    
    if (error) {
      throw error;
    }
    // Success toast handled centrally by persistence actions with authoritative backend counts
    logger.info('executeSaveOperation', 'Save operation completed successfully');
    
  } catch (error) {
    logger.error('executeSaveOperation', 'Save operation failed', { error });
    toast.error('Failed to save account data');
  } finally {
    setProcessingState(ConnectionProcessingState.IDLE);
    persistenceExecutionRef.current.delete(executionKey);
  }
}

/**
 * Helper function to handle delete operations for Wealthica accounts
 * Extracted to reduce main function size and improve maintainability
 */
export async function executeDeleteOperation(
  currentUserId: string,
  wealthicaConnectionData: WealthicaConnection,
  persistenceExecutionRef: React.MutableRefObject<Set<string>>,
  setProcessingState: (state: string) => void,
  queryClient: QueryClient
): Promise<void> {
  if (!currentUserId || !wealthicaConnectionData) {
    logger.error('executeDeleteOperation', 'Cannot delete - missing required data');
    return;
  }

  // Execution guard to prevent duplicate deletes
  const executionKey = `delete_${currentUserId}_${wealthicaConnectionData.institutionId}_${Date.now()}`;
  if (persistenceExecutionRef.current.has(executionKey)) {
    logger.warn('executeDeleteOperation', 'Delete already in progress, skipping duplicate');
    return;
  }
  
  persistenceExecutionRef.current.add(executionKey);
  
  try {
    setProcessingState(ConnectionProcessingState.DISCONNECTING);
    
    logger.info('executeDeleteOperation', 'Starting delete operation', {
      userId: currentUserId,
      institutionId: wealthicaConnectionData.institutionId,
      executionKey
    });

    // Delete accounts using Supabase edge function
    const { error } = await supabase.functions.invoke('delete-wealthica-data', {
      body: {
        userId: currentUserId,
        institutionId: wealthicaConnectionData.institutionId
      }
    });
    
    if (error) {
      throw error;
    }
    
    // Invalidate queries after successful delete
    queryClient.invalidateQueries({ queryKey: ['wealthica2', 'accounts'] });
    queryClient.invalidateQueries({ queryKey: ['multiWealthica2', 'accounts'] });
    queryClient.invalidateQueries({ queryKey: ['wealthica2', 'positions'] });
    queryClient.invalidateQueries({ queryKey: ['multiWealthica2', 'positions'] });
    // Success toast handled centrally by persistence actions with authoritative backend counts
    logger.info('executeDeleteOperation', 'Delete operation completed successfully');
    
  } catch (error) {
    logger.error('executeDeleteOperation', 'Delete operation failed', { error });
    toast.error('Failed to delete account data');
  } finally {
    setProcessingState(ConnectionProcessingState.IDLE);
    persistenceExecutionRef.current.delete(executionKey);
  }
}
